import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  mergeClasses,
  webLightTheme,
  FluentProvider,
} from '@fluentui/react-components';
import { TextareaValueData } from '@fluentai/textarea';
import { CopilotProvider } from '@fluentai/react-copilot';
import { I18nProvider, DEFAULT_LOCALE } from '@bingads-webui/copilot-i18n';
import { Header } from '@bingads-webui-react/copilot-ui-base';
import { CopilotResizer } from '@bingads-webui-react/copilot-resizer';
import {
  useStructuredLog,
  dispatchStructuredLogMessageEvent,
  dispatchStructuredLogUserEngagementEvent,
} from '@bingads-webui-react/copilot-logger';
import './global';
import { ConversationsList } from './components/conversation-list/conversations-list';

import { type IAdsCopilotProps, type IConversationThreadProps } from './types';
import { AutoScroller } from './AutoScroller';
import { ConversationThread, DEFAULT_GROUP_TIME } from './components/conversation-thread';
import { DirectLineProvider } from './components/direct-line-provider';
import { useAdsCopilotContext } from './components/ads-copilot-context-provider';
import { useStaticMessage } from './hooks/useStaticMessage';
import { useSesId } from './hooks/useSesId';
import { useChatSuggestions } from './hooks/useChatSuggestions';
import { useInputDisabled } from './hooks/useInputDisabled';
import { useAdsCopilotStyles } from './hooks/useAdsCopilotStyles';
import { usePostMessage } from './hooks/use-post-message';
import { ChatInput } from './ChatInput';
import { DirectLineErrorView } from './ErrorView';
import { type BotActivity, IDirectLineError } from './botTypes';
import { PromptBar } from './PromptBar';
import saveConversation from './components/direct-line-provider/chat-history-direct-line';

const COPILOT_WIDTH = 320;

const AdsCopilotInternal: FC<IAdsCopilotProps> = memo((props) => {
  const {
    onClose,
    botServiceConfig,
    onConnectionStatusChange,
    groupTime,
    renderAction,
    onSendActivityError,
    onFeedbackButtonClick,
    renderHeaderComponent,
    renderFooterComponent,
    renderTopComponent,
    renderBottomComponent,
    onReferenceClick,
    onSupportClick,
    showPreviewTag,
    showSupportButton,
    maxInputLength,
    headerContent,
    title,
    customStream,
    isTokenExpiredAutoReloadEnabled,
    onAutoReloading,
    isStopGeneratingEnabled,
    showViewPrompt,
    onViewPromptClick,
    isChatSuggestionsEnabled,
    isChatHistoryEnabled,
    isTroubleshootingEnabled,
    isReportingInsightsEnabled,
    isUxImprovementsEnabled: isUxImprovementsEnabledProp,
    onChatSuggestionClick,
    renderCustomErrorView,
    onChangeChatInput,
    isCopilotDegraded,
    isShowFeaturesInHint,
    onAIDegrade,
    isTroubleshootingIntentionDetectionEnabled,
    logger,
    onReceiveActivityEnd,
    showHeader,
    shouldDisableInput,
    locale,
  } = props;
  const autoScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const resetConversation = React.useRef<boolean>(false);
  const conversationIdRef = useRef<string>('');

  /* Registers event listeners for structured log */
  useStructuredLog({
    log: (data) => {
      logger?.trace(`[Structured log] ${JSON.stringify(data)}`);
    },
    error: (data, errors) => {
      logger?.error(`[Structured log] ${JSON.stringify({ ...data, errors })}`);
    },
  });

  const styles = useAdsCopilotStyles();
  const {
    sesId, onClickStartOver, onInitDirectLine, directLine,
  } = useSesId(props);
  const [chatInputValue, setChatInputValue] = useState('');
  const [showChatHistory, setShowChatHistory] = useState(false);
  const existingToken = useRef<string | null>(null);
  const activeConversation = useRef<string | null>(null);

  // Handle showing the Chat History popover
  const handleChatHistoryClick = useCallback(() => {
    setShowChatHistory(true);
  }, [showChatHistory]);

  const handleChatHistoryClose = useCallback(() => {
    setShowChatHistory(false);

    /* NOTE: This is needed for now as the AdsCopilot component is being
       unmounted when ChatHistory is loaded. There's a bug ticket to fix this
       in the future: Work item 8440464 */
    onClickStartOver();
  }, [showChatHistory]);

  const handleChatHistoryResetConversation = useCallback((conversationToken: string) => {
    if (activeConversation.current && existingToken.current) {
      saveConversation(existingToken.current, activeConversation.current || 'Untitled');
    }
    handleChatHistoryClose();
    existingToken.current = conversationToken;
  }, [existingToken]);

  const onClickStartOverWithResetConversation = useCallback(() => {
    resetConversation.current = true;
    if (isChatHistoryEnabled && activeConversation.current) {
      activeConversation.current = null;
    }
    onClickStartOver();
  }, [onClickStartOver]);

  const {
    renderActivity,
  } = useStaticMessage({
    ...props,
    onClickStartOver: onClickStartOverWithResetConversation,
  });

  const {
    sendDisabled,
    inputDisabled,
    onSendActivity,
    onReceiveActivity,
  } = useInputDisabled({
    ...props,
    botServiceConfig,
  });

  const {
    postMessage,
  } = usePostMessage({
    ...props,
    directLine,
    isCustomStream: !!customStream,
    botServiceConfig,
    onSendActivity,
    onSendActivityError,
  });

  const onChangeChatInputInternal = useCallback((_ev: any, { value: v }: TextareaValueData) => {
    setChatInputValue(v);
    onChangeChatInput?.();
  }, [onChangeChatInput]);

  const handleChatInputPostMessage = useCallback((_evt: any, { value }: TextareaValueData) => {
    if (postMessage(_evt, { value })) {
      setChatInputValue('');
      if (isChatHistoryEnabled && !activeConversation.current) {
        activeConversation.current = value;
      }
    }
  }, [postMessage]);

  const handleConversationThreadSuggestionClick = useCallback<Exclude<IConversationThreadProps['onSuggestionClick'], undefined>>((_evt, value, messageOptions) => {
    if (postMessage(_evt, { value }, messageOptions)) {
      setChatInputValue('');
      if (isChatHistoryEnabled && !activeConversation.current) {
        activeConversation.current = value;
      }
    }
  }, [postMessage]);

  const handleChatSuggestionClick = useCallback((value: string) => {
    onChatSuggestionClick?.(value);
    if (postMessage(null, { value })) {
      setChatInputValue('');
      if (isChatHistoryEnabled && !activeConversation.current) {
        activeConversation.current = value;
      }
    }
  }, [postMessage, onChatSuggestionClick]);

  const {
    onReceiveActivity: onReceiveActivityWithChatSuggestions,
    renderChatSuggestionList,
  } = useChatSuggestions({
    onReceiveActivity,
    handleChatSuggestionClick,
    isChatSuggestionsEnabled,
  });

  const renderDirectLineErrorView = useCallback(
    (error: IDirectLineError) => (
      <DirectLineErrorView
        error={error}
        onRefresh={onClickStartOverWithResetConversation}
        isTokenExpiredAutoReloadEnabled={isTokenExpiredAutoReloadEnabled}
        onAutoReloading={onAutoReloading}
      />
    ),
    [onClickStartOverWithResetConversation, isTokenExpiredAutoReloadEnabled, onAutoReloading],
  );

  const onReceiveActivityStart = useCallback((
    activity: BotActivity,
    finalActivities: BotActivity[],
  ) => {
    onAIDegrade?.(activity, finalActivities, postMessage);

    /* Stores the conversationId for structured log when live chat is not enabled.
       This will be used to override the conversationId in structured log during live chat. */
    if (!customStream) {
      conversationIdRef.current = activity.conversation?.id!;
    }

    dispatchStructuredLogMessageEvent({
      activity,
      botServiceConfig,
      locale: locale as string,
      options: {
        copilotConversationId: conversationIdRef.current,
      },
    });
    dispatchStructuredLogUserEngagementEvent({
      activity,
      botServiceConfig,
      locale: locale as string,
    });
  }, [onAIDegrade, postMessage, botServiceConfig, customStream, locale]);

  const adsCopilotContext = useAdsCopilotContext();
  useEffect(() => {
    adsCopilotContext?.onBotServiceConfigChange?.(botServiceConfig);
  }, [adsCopilotContext?.onBotServiceConfigChange, botServiceConfig]);
  const isUxImprovementsEnabled = isUxImprovementsEnabledProp || adsCopilotContext?.pilotMap?.isUxImprovementsEnabled;
  const isMarketingIntegrationEnabled = adsCopilotContext?.pilotMap?.isMarketingIntegrationEnabled;
  return (
    <DirectLineProvider
      {...botServiceConfig}
      sesId={sesId}
      renderErrorView={renderDirectLineErrorView}
      renderCustomErrorView={renderCustomErrorView}
      onInitDirectLine={onInitDirectLine}
      onConnectionStatusChange={onConnectionStatusChange}
      customStream={customStream}
      resetConversation={resetConversation}
      isChatHistoryEnabled={isChatHistoryEnabled}
      existingToken={existingToken}
      conversationTitle={activeConversation.current || 'Untitled'}
      logger={logger}
      isMarketingIntegrationEnabled={isMarketingIntegrationEnabled}
    >
      {(!isChatHistoryEnabled || !showChatHistory) && (
      <div className={mergeClasses(styles.root, 'ads-copilot-root')}>
        {showHeader && (
        <Header
          showPreviewTag={showPreviewTag}
          showSupportButton={showSupportButton}
          onSupportClick={onSupportClick}
          onRefresh={onClickStartOverWithResetConversation}
          onClose={onClose}
          title={title}
          onChatHistoryClick={handleChatHistoryClick}
          isChatHistoryEnabled={isChatHistoryEnabled}
          isUxImprovementsEnabled={isUxImprovementsEnabled}
        >
          {headerContent}
        </Header>
        )}
        {renderHeaderComponent?.()}
        <div className={mergeClasses(styles.container, 'chat-container')}>
          <AutoScroller className="conversation-history-container" autoScrollContainerRef={autoScrollContainerRef}>
            <ConversationThread
              ref={autoScrollContainerRef}
              onReceiveActivity={onReceiveActivityWithChatSuggestions}
              onReceiveActivityEnd={onReceiveActivityEnd}
              onReceiveActivityStart={onReceiveActivityStart}
              renderActivity={renderActivity}
              groupTime={groupTime}
              renderTopComponent={renderTopComponent}
              renderAction={renderAction}
              onFeedbackButtonClick={onFeedbackButtonClick}
              onReferenceClick={onReferenceClick}
              renderBottomComponent={renderBottomComponent}
              isCustomStream={!!customStream}
              isStopGeneratingEnabled={isStopGeneratingEnabled}
              isTroubleshootingEnabled={isTroubleshootingEnabled}
              isReportingInsightsEnabled={isReportingInsightsEnabled}
              isTroubleshootingIntentionDetectionEnabled={isTroubleshootingIntentionDetectionEnabled}
              onSuggestionClick={handleConversationThreadSuggestionClick}
              renderChatSuggestionList={renderChatSuggestionList}
              isCopilotDegraded={isCopilotDegraded}
              isShowFeaturesInHint={isShowFeaturesInHint}
            />
          </AutoScroller>
          {renderFooterComponent?.()}
          {showViewPrompt && <PromptBar onViewPromptClick={onViewPromptClick} />}
          <ChatInput
            inputDisabled={inputDisabled || shouldDisableInput}
            sendDisabled={sendDisabled}
            maxLength={maxInputLength}
            onChange={onChangeChatInputInternal}
            postMessage={handleChatInputPostMessage}
            value={chatInputValue}
          />
        </div>
      </div>
      )}
      {showChatHistory && (
        <div style={{ position: 'relative' }}>
          <ConversationsList
            isOpen={showChatHistory}
            onClose={handleChatHistoryClose}
            onResetConversation={handleChatHistoryResetConversation}
          />
        </div>
      )}
    </DirectLineProvider>
  );
});

const AdsCopilotBase: FC<IAdsCopilotProps> = memo((props) => {
  const {
    bingAdsI18n,
    className,
    currencyCode,
    locale,
    theme,
  } = props;
  const styles = useAdsCopilotStyles();

  return (
    <I18nProvider bingAdsI18n={bingAdsI18n} currencyCode={currencyCode} locale={locale}>
      <FluentProvider theme={theme} className={mergeClasses(styles.provider, className, 'ads-copilot')} applyStylesToPortals={false}>
        <CopilotProvider
          className={styles.wrapper}
          mode="sidecar"
        >
          <AdsCopilotInternal {...props} />
        </CopilotProvider>
      </FluentProvider>
    </I18nProvider>
  );
});

export const AdsCopilot: FC<IAdsCopilotProps> = memo((props) => {
  const { enableResizer, resizerProps } = props;

  if (enableResizer) {
    return (
      <CopilotResizer {...resizerProps}>
        <AdsCopilotBase {...props} />
      </CopilotResizer>
    );
  }

  return (
    <div style={{ width: COPILOT_WIDTH, height: '100%' }}>
      <AdsCopilotBase {...props} />
    </div>
  );
});

AdsCopilotBase.defaultProps = {
  onInitDirectLine: undefined,
  onConnectionStatusChange: undefined,
  onReceiveActivity: undefined,
  onClose: undefined,
  renderActivity: undefined,
  onSendActivity: undefined,
  onSendActivityError: undefined,
  renderBottomComponent: undefined,
  renderAction: undefined,
  onFeedbackButtonClick: undefined,
  groupTime: DEFAULT_GROUP_TIME,
  locale: DEFAULT_LOCALE,
  className: '',
  showPreviewTag: true,
  maxInputLength: 500,
  headerContent: undefined,
  onReferenceClick: undefined,
  theme: webLightTheme,
  onStartOverClick: undefined,
  isTokenExpiredAutoReloadEnabled: false,
  onAutoReloading: undefined,
  showViewPrompt: false,
  onViewPromptClick: undefined,
  isChatSuggestionsEnabled: false,
  renderCustomErrorView: undefined,
  isCopilotDegraded: false,
  onAIDegrade: undefined,
  showHeader: true,
  shouldDisableInput: false,
};

AdsCopilot.defaultProps = {
  enableResizer: false,
  resizerProps: {},
};
